import React from 'react';
import {
  Phone, ShieldCheck, Workflow, BarChart3, Layers, Plug,
  PhoneOff, AlertTriangle, Activity, TrendingUp, EyeOff, GitBranch,
  MapPin, Shield, FileCheck, Signal, Building2,
  Radio, AudioLines, Mic, ArrowRight, Check, Zap } from
'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { WaitlistForm } from './WaitlistForm';
import { VakyaLogo } from './VakyaLogo';
import { featurePillars, painPoints, indiaAdvantages, codeExamples } from '../data/mock';

const iconMap = {
  'phone': Phone,
  'shield-check': ShieldCheck,
  'workflow': Workflow,
  'bar-chart-3': BarChart3,
  'layers': Layers,
  'plug': Plug,
  'phone-off': PhoneOff,
  'alert-triangle': AlertTriangle,
  'activity': Activity,
  'trending-up': TrendingUp,
  'eye-off': EyeOff,
  'git-branch': GitBranch,
  'map-pin': MapPin,
  'shield': Shield,
  'file-check': FileCheck,
  'signal': Signal,
  'building-2': Building2,
  'radio': Radio,
  'audio-lines': AudioLines,
  'mic': Mic
};

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-lg border-b border-white/5">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <VakyaLogo className="w-10 h-8" />
              <span className="text-xl font-bold">Awaaz AI Telephony</span>
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-300 hover:text-white transition-colors">Features</a>
              <a href="#developer" className="text-gray-300 hover:text-white transition-colors">Developers</a>
              <a href="#pricing" className="text-gray-300 hover:text-white transition-colors">Pricing</a>
              <a href="#waitlist" className="text-gray-300 hover:text-white transition-colors">Docs</a>
              <a href="#waitlist" className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-primary-foreground h-9 px-3">
                Get Access
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-8">
            <Badge className="bg-cyan-500/10 text-cyan-400 border-cyan-500/20 hover:bg-cyan-500/20">
              Production Voice AI Infrastructure for India
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold leading-tight">
              You Build the Agent.<br />
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                We Make it Production-Ready
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
              End-to-end telephony and engagement orchestration platform for Voice AI teams.
              Handle production-grade inbound and outbound calling at scale — built for India's regulatory environment.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-lg px-8">
                Get API Access
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5 text-lg px-8" asChild>
                <a href="https://docs.awaaz.de/" target="_blank" rel="noopener noreferrer">
                  View Documentation
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-6 bg-slate-900/50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Productionizing Voice AI is <span className="text-red-400">Painful</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Building the agent is already hard. Taking it to production over the phone shouldn't be.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {painPoints.map((pain, index) => {
              const Icon = iconMap[pain.icon];
              return (
                <Card key={index} className="bg-slate-800/50 border-slate-700/50 hover:border-red-500/30 transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-red-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold mb-3 text-white">{pain.title}</h3>
                        <ul className="space-y-2">
                          {pain.points.map((point, idx) =>
                          <li key={idx} className="text-sm text-gray-400 flex items-start gap-2">
                              <span className="text-red-400 mt-1">•</span>
                              <span>{point}</span>
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>);

            })}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              The Production Orchestration Layer<br />
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                for Voice AI Engagement
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              A unified platform that handles everything required to run Voice AI at scale — except building the agent itself.
            </p>
          </div>
        </div>
      </section>

      {/* Feature Pillars */}
      <section id="features" className="py-20 px-6 bg-slate-900/30">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featurePillars.map((pillar) => {
              const Icon = iconMap[pillar.icon];
              return (
                <Card key={pillar.id} className="bg-slate-800/50 border-slate-700/50 hover:border-cyan-500/50 transition-all duration-300 hover:transform hover:scale-105">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-600/20 flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-cyan-400" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3 text-white">{pillar.title}</h3>
                    <p className="text-cyan-400 text-sm font-medium mb-4">{pillar.description}</p>
                    <ul className="space-y-2">
                      {pillar.features.map((feature, idx) =>
                      <li key={idx} className="text-gray-400 flex items-start gap-2">
                          <Check className="w-4 h-4 text-cyan-400 mt-1 flex-shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      )}
                    </ul>
                  </CardContent>
                </Card>);

            })}
          </div>
        </div>
      </section>

      {/* Developer Experience */}
      <section id="developer" className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Developer-First Experience
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Clean REST APIs, webhooks, and SDKs. Get started in minutes.
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-cyan-400">Make a Call</h3>
              <div className="bg-slate-950 rounded-lg p-6 border border-slate-800">
                <pre className="text-sm text-gray-300 overflow-x-auto">
                  <code>{codeExamples.rest}</code>
                </pre>
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-cyan-400">Handle Events</h3>
              <div className="bg-slate-950 rounded-lg p-6 border border-slate-800">
                <pre className="text-sm text-gray-300 overflow-x-auto">
                  <code>{codeExamples.webhook}</code>
                </pre>
              </div>
            </div>
          </div>
          <div className="mt-12 text-center">
            <div className="inline-flex flex-col items-center gap-6 bg-slate-800/50 rounded-lg px-8 py-6 border border-slate-700/50">
              <span className="text-gray-300 text-sm">Works seamlessly with:</span>
              <div className="flex flex-wrap items-center justify-center gap-8">
                <a
                  href="https://livekit.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-all duration-300 opacity-50 hover:opacity-100">

                  <img
                    src="https://customer-assets.emergentagent.com/job_agent-infra-2/artifacts/ask9k6fg_image.png"
                    alt="LiveKit"
                    className="h-7 w-auto brightness-90 contrast-125 saturate-0 hover:saturate-100 hover:brightness-110 transition-all duration-300" />

                </a>
                <a
                  href="https://www.pipecat.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-all duration-300 opacity-50 hover:opacity-100">

                  <img
                    src="https://customer-assets.emergentagent.com/job_agent-infra-2/artifacts/g92w2zla_image.png"
                    alt="Pipecat"
                    className="h-7 w-auto brightness-90 contrast-125 saturate-0 hover:saturate-100 hover:brightness-110 transition-all duration-300" />

                </a>
                <a
                  href="https://elevenlabs.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-all duration-300 opacity-50 hover:opacity-100">

                  <img
                    src="https://customer-assets.emergentagent.com/job_agent-infra-2/artifacts/7a416qy5_image.png"
                    alt="ElevenLabs"
                    className="h-8 w-auto brightness-90 contrast-125 saturate-0 hover:saturate-100 hover:brightness-110 transition-all duration-300" />

                </a>
                <a
                  href="https://vapi.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-all duration-300 opacity-50 hover:opacity-100">

                  <img
                    src="https://customer-assets.emergentagent.com/job_agent-infra-2/artifacts/em1q8hxl_image.png"
                    alt="Vapi"
                    className="h-9 w-auto brightness-90 contrast-125 saturate-0 hover:saturate-100 hover:brightness-110 transition-all duration-300" />

                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* India Advantage */}
      <section className="py-20 px-6 bg-gradient-to-br from-orange-500/10 to-green-500/10">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 mb-6">
              Built for India
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              India-First Infrastructure
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              TRAI-aware, DLT-ready, and optimized for Indian enterprises
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {indiaAdvantages.map((advantage, index) => {
              const Icon = iconMap[advantage.icon];
              return (
                <Card key={index} className="bg-slate-800/50 border-slate-700/50 text-center">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-orange-500/10 flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-6 h-6 text-orange-400" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2 text-white">{advantage.title}</h3>
                    <p className="text-sm text-gray-400">{advantage.description}</p>
                  </CardContent>
                </Card>);

            })}
          </div>
        </div>
      </section>

      {/* Pricing Teaser */}
      <section id="pricing" className="py-20 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Usage-Based Pricing
          </h2>
          <p className="text-xl text-gray-400 mb-8">
            Pay only for what you use. Scale seamlessly from first call to millions.
          </p>
          <a href="#waitlist" className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-primary-foreground h-12 px-8 text-lg">
            Contact Sales
          </a>
        </div>
      </section>

      {/* Waitlist CTA */}
      <section id="waitlist" className="py-20 px-6 bg-gradient-to-br from-cyan-500/10 to-blue-600/10">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Go Production?
          </h2>
          <p className="text-xl text-gray-400 mb-12">
            Join the waitlist for early API access and build Voice AI that scales.
          </p>
          <WaitlistForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-slate-950 border-t border-white/5">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <VakyaLogo className="w-8 h-6" />
                <span className="text-lg font-bold">Awaaz AI Telephony</span>
              </div>
              <p className="text-gray-400 text-sm">Production Voice AI infra for India

              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#developer" className="hover:text-white transition-colors">Developers</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="https://docs.awaaz.de/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="mailto:support@awaaz.ai" className="hover:text-white transition-colors">Support</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="https://awaaz.ai/#Team" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">About</a></li>
                <li><a href="https://awaaz.ai/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="https://awaaz.ai/tos" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-white/5 text-center text-gray-400 text-sm">
            <p>&copy; 2026 Awaaz AI. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>);

};

export default LandingPage;